/**
 * Provilan Cart — Drawer + Add-to-Cart Interception.
 * Public API on window.ProvilanCart: open(), close(), refresh(), add(id, qty), applyDiscount(code).
 */
(function () {
  'use strict';

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var locale = (document.documentElement.lang || 'de').replace('_', '-');
    try {
      return (cents / 100).toLocaleString(locale, { style: 'currency', currency: currency });
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  function setState(el, state) {
    if (!el) return;
    el.dataset.state = state;
    el.setAttribute('aria-hidden', state === 'open' ? 'false' : 'true');
  }

  var Cart = {
    drawer: null,
    panel: null,
    backdrop: null,
    itemsEl: null,
    emptyEl: null,
    footerEl: null,
    subtotalEl: null,
    shippingBarEl: null,
    shippingMessageEl: null,
    shippingFillEl: null,
    discountFormEl: null,
    discountInputEl: null,
    discountErrorEl: null,
    discountAppliedEl: null,
    savingsEl: null,
    savingsAmountEl: null,
    freeShippingEnabled: false,
    freeShippingThreshold: 0,
    state: null,

    init: function () {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;
      this.panel = this.drawer.querySelector('[data-cart-panel]');
      this.backdrop = this.drawer.querySelector('[data-cart-backdrop]');
      this.itemsEl = this.drawer.querySelector('[data-cart-items]');
      this.emptyEl = this.drawer.querySelector('[data-cart-empty]');
      this.footerEl = this.drawer.querySelector('[data-cart-footer]');
      this.subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
      this.shippingBarEl = this.drawer.querySelector('[data-cart-shipping-bar]');
      this.shippingMessageEl = this.drawer.querySelector('[data-cart-shipping-message]');
      this.shippingFillEl = this.drawer.querySelector('[data-cart-shipping-fill]');
      this.discountFormEl = this.drawer.querySelector('[data-cart-discount-form]');
      this.discountInputEl = this.drawer.querySelector('#cart-discount-input');
      this.discountErrorEl = this.drawer.querySelector('[data-cart-discount-error]');
      this.discountAppliedEl = this.drawer.querySelector('[data-cart-discount-applied]');
      this.savingsEl = this.drawer.querySelector('[data-cart-savings]');
      this.savingsAmountEl = this.savingsEl ? this.savingsEl.querySelector('span:last-child') : null;
      this.freeShippingEnabled = this.drawer.dataset.freeShippingEnabled === 'true';
      this.freeShippingThreshold = parseInt(this.drawer.dataset.freeShippingThreshold || '0', 10);
      this.bindEvents();
      this.refresh();
    },

    open: function () {
      document.body.style.overflow = 'hidden';
      setState(this.drawer, 'open');
      setState(this.panel, 'open');
      setState(this.backdrop, 'open');
    },

    close: function () {
      document.body.style.overflow = '';
      setState(this.drawer, 'closed');
      setState(this.panel, 'closed');
      setState(this.backdrop, 'closed');
    },

    add: function (id, quantity) {
      var self = this;
      quantity = quantity || 1;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || err.message || 'Cart add failed');
            });
          }
          return res.json();
        })
        .then(function () {
          return self.refresh();
        })
        .then(function () {
          self.open();
        });
    },

    changeByKey: function (key, quantity) {
      var self = this;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (res) {
        if (res.ok) return self.refresh();
      });
    },

    /**
     * Setzt den Rabattcode über /cart/update.js (Ajax Cart API, Parameter
     * `discount`) und prüft danach am Warenkorb, ob er greift.
     *
     * Zwei Fallstricke, die hier bewusst gelöst sind:
     * 1. `discount` ERSETZT die Codeliste (leerer String löscht alle). Der
     *    frühere Weg über /discount/{code} hat Codes stattdessen angehängt —
     *    ein alter, nicht kombinierbarer Code blieb im Warenkorb kleben und
     *    machte jeden neuen Code dauerhaft „ungültig".
     * 2. Geprüft wird discount_codes[].applicable, NICHT total_discount:
     *    Versandrabatte erzeugen im Warenkorb keinen Abzug, weil /cart.js
     *    keine Versandkosten kennt — die entstehen erst im Checkout. Über
     *    total_discount gemessen sähe ein gültiger Versandcode ungültig aus.
     */
    applyDiscount: function (code) {
      var self = this;
      code = String(code || '').trim();
      if (!code) return Promise.resolve();
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code })
      })
        .then(function () { return self.refresh(); })
        .then(function () {
          var entered = code.toUpperCase();
          var codes = (self.state && self.state.discount_codes) || [];
          var match = null;
          codes.forEach(function (d) {
            if (String(d.code).toUpperCase() === entered) match = d;
          });
          if (!match || !match.applicable) {
            throw new Error('Code ungültig oder nicht anwendbar.');
          }
        });
    },

    refresh: function () {
      var self = this;
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.state = cart;
          self.render(cart);
          self.updateBubble(cart.item_count);
        });
    },

    updateBubble: function (count) {
      var counts = document.querySelectorAll('[data-cart-count]');
      counts.forEach(function (el) { el.textContent = count; });
      var bubbles = document.querySelectorAll('.cart-count-bubble');
      bubbles.forEach(function (el) {
        if (count > 0) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    },

    renderShippingBar: function (cart) {
      if (!this.shippingBarEl || !this.shippingMessageEl || !this.shippingFillEl) return;
      if (!this.freeShippingEnabled || this.freeShippingThreshold <= 0 || cart.item_count === 0) {
        this.shippingBarEl.setAttribute('hidden', '');
        return;
      }
      this.shippingBarEl.removeAttribute('hidden');
      var subtotal = cart.items_subtotal_price;
      var remaining = Math.max(0, this.freeShippingThreshold - subtotal);
      var progress = Math.min(100, Math.round((subtotal / this.freeShippingThreshold) * 100));
      this.shippingFillEl.style.width = progress + '%';
      if (remaining <= 0) {
        this.shippingMessageEl.innerHTML =
          '<span class="font-semibold text-[var(--color-brand-primary)]">Sie erhalten kostenlosen Versand.</span>';
      } else {
        this.shippingMessageEl.innerHTML =
          'Noch <span class="font-semibold text-stone-900">' + escapeHtml(formatMoney(remaining)) +
          '</span> bis zum kostenlosen Versand.';
      }
    },

    renderDiscount: function (cart) {
      if (!this.discountAppliedEl) return;
      var applied = cart.cart_level_discount_applications || [];

      // title einer Code-Discount-Application ist der Code selbst — darüber
      // lässt sich der Betrag dem Code zuordnen. Produktrabatte sitzen nicht
      // auf Warenkorb-, sondern auf Zeilenebene und werden hier aufsummiert;
      // was danach ohne Betrag übrig bleibt, ist ein Versandrabatt.
      var amounts = {};
      applied.forEach(function (d) { amounts[String(d.title).toUpperCase()] = d.total_allocated_amount; });
      (cart.items || []).forEach(function (item) {
        (item.line_level_discount_allocations || []).forEach(function (a) {
          var key = String(a.discount_application && a.discount_application.title).toUpperCase();
          amounts[key] = (amounts[key] || 0) + a.amount;
        });
      });

      var rows = [];
      var seen = {};
      (cart.discount_codes || []).forEach(function (d) {
        if (!d.applicable) return;
        var key = String(d.code).toUpperCase();
        seen[key] = true;
        rows.push({ title: d.code, amount: amounts.hasOwnProperty(key) ? amounts[key] : null });
      });
      // Automatische Rabatte haben keinen Code und tauchen nur hier auf.
      applied.forEach(function (d) {
        if (seen[String(d.title).toUpperCase()]) return;
        rows.push({ title: d.title, amount: d.total_allocated_amount });
      });

      if (rows.length === 0) {
        this.discountAppliedEl.innerHTML = '';
        this.discountAppliedEl.setAttribute('hidden', '');
      } else {
        this.discountAppliedEl.innerHTML = rows.map(function (d) {
          var value = d.amount === null
            ? '<span class="font-semibold">Keine Versandkosten</span>'
            : '<span class="font-bold">−' + escapeHtml(formatMoney(d.amount)) + '</span>';
          return (
            '<li class="flex items-center justify-between gap-3 rounded-lg bg-emerald-50 px-3 py-2 font-meta text-sm text-emerald-800">' +
              '<span class="flex items-center gap-2">' +
                '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8l3.5 3.5L13 5"/></svg>' +
                '<span class="font-semibold">' + escapeHtml(d.title) + '</span>' +
              '</span>' +
              value +
            '</li>'
          );
        }).join('');
        this.discountAppliedEl.removeAttribute('hidden');
      }

      if (this.savingsEl && this.savingsAmountEl) {
        if (cart.total_discount > 0) {
          this.savingsAmountEl.textContent = formatMoney(cart.items_subtotal_price);
          this.savingsEl.removeAttribute('hidden');
        } else {
          this.savingsEl.setAttribute('hidden', '');
        }
      }
    },

    render: function (cart) {
      if (!this.itemsEl || !this.emptyEl || !this.footerEl) return;
      if (cart.item_count === 0) {
        this.emptyEl.removeAttribute('hidden');
        this.itemsEl.setAttribute('hidden', '');
        this.footerEl.setAttribute('hidden', '');
        if (this.shippingBarEl) this.shippingBarEl.setAttribute('hidden', '');
        return;
      }
      this.emptyEl.setAttribute('hidden', '');
      this.itemsEl.removeAttribute('hidden');
      this.footerEl.removeAttribute('hidden');

      var html = cart.items.map(function (item) {
        var image = item.image
          ? '<img src="' + escapeHtml(item.image) + '&width=240" alt="' + escapeHtml(item.title) + '" class="max-h-full max-w-full object-contain" loading="lazy">'
          : '';
        var variantTitle = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<span class="mt-1 font-meta text-base text-stone-500">' + escapeHtml(item.variant_title) + '</span>'
          : '';
        return (
          '<li class="flex gap-5 py-6" data-cart-line="' + escapeHtml(item.key) + '">' +
            '<div class="grid h-24 w-24 shrink-0 place-items-center rounded-xl bg-stone-50 p-2">' + image + '</div>' +
            '<div class="flex flex-1 flex-col">' +
              '<a href="' + escapeHtml(item.url) + '" class="font-heading text-lg font-semibold leading-tight text-stone-900 hover:text-[var(--color-brand-primary)]">' + escapeHtml(item.product_title) + '</a>' +
              variantTitle +
              '<div class="mt-3 flex items-center gap-3">' +
                '<div class="inline-flex items-center rounded-full border border-stone-200">' +
                  '<button type="button" data-cart-decrease aria-label="Weniger" class="grid h-9 w-9 place-items-center text-xl leading-none text-stone-500 hover:text-stone-900">−</button>' +
                  '<span data-cart-quantity class="w-9 text-center font-display text-base font-bold">' + item.quantity + '</span>' +
                  '<button type="button" data-cart-increase aria-label="Mehr" class="grid h-9 w-9 place-items-center text-xl leading-none text-stone-500 hover:text-stone-900">+</button>' +
                '</div>' +
                '<span class="ml-auto font-display text-base font-bold text-stone-900">' + formatMoney(item.final_line_price) + '</span>' +
                '<button type="button" data-cart-remove aria-label="Entfernen" class="text-stone-400 transition hover:text-stone-700">' +
                  '<svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 4l8 8M12 4L4 12"/></svg>' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</li>'
        );
      }).join('');

      this.itemsEl.innerHTML = html;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
      this.renderShippingBar(cart);
      this.renderDiscount(cart);
    },

    bindEvents: function () {
      var self = this;

      document.addEventListener('click', function (e) {
        var toggle = e.target.closest && e.target.closest('[data-cart-toggle]');
        if (toggle) {
          e.preventDefault();
          self.open();
          return;
        }
        var closeEl = e.target.closest && e.target.closest('[data-cart-close]');
        if (closeEl) {
          e.preventDefault();
          self.close();
          return;
        }
        var backdrop = e.target.closest && e.target.closest('[data-cart-backdrop]');
        if (backdrop) {
          self.close();
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer && self.drawer.dataset.state === 'open') {
          self.close();
        }
      });

      document.addEventListener('submit', function (e) {
        var form = e.target.closest && e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        var fd = new FormData(form);
        var id = fd.get('id');
        if (!id) return;
        var qty = parseInt(fd.get('quantity') || '1', 10) || 1;
        var btn = form.querySelector('[data-product-submit]');
        if (btn) btn.disabled = true;
        self.add(id, qty)
          .catch(function (err) {
            console.error('[ProvilanCart] add failed:', err);
            alert(err.message || 'Hinzufügen fehlgeschlagen');
          })
          .finally(function () {
            if (btn) btn.disabled = false;
          });
      });

      if (this.discountFormEl) {
        this.discountFormEl.addEventListener('submit', function (e) {
          e.preventDefault();
          var code = self.discountInputEl ? self.discountInputEl.value.trim() : '';
          if (!code) return;
          var btn = self.discountFormEl.querySelector('[data-cart-discount-apply]');
          if (btn) btn.disabled = true;
          if (self.discountInputEl) self.discountInputEl.disabled = true;
          if (self.discountErrorEl) {
            self.discountErrorEl.setAttribute('hidden', '');
            self.discountErrorEl.textContent = '';
          }
          self.applyDiscount(code)
            .then(function () {
              if (self.discountInputEl) self.discountInputEl.value = '';
            })
            .catch(function (err) {
              if (self.discountErrorEl) {
                self.discountErrorEl.textContent = err.message || 'Code konnte nicht angewendet werden.';
                self.discountErrorEl.removeAttribute('hidden');
              }
            })
            .finally(function () {
              if (btn) btn.disabled = false;
              if (self.discountInputEl) self.discountInputEl.disabled = false;
            });
        });
      }

      if (this.drawer) {
        this.drawer.addEventListener('click', function (e) {
          var line = e.target.closest && e.target.closest('[data-cart-line]');
          if (!line) return;
          var key = line.dataset.cartLine;
          var qtySpan = line.querySelector('[data-cart-quantity]');
          var current = qtySpan ? parseInt(qtySpan.textContent, 10) : 0;

          if (e.target.closest('[data-cart-remove]')) {
            self.changeByKey(key, 0);
          } else if (e.target.closest('[data-cart-increase]')) {
            self.changeByKey(key, current + 1);
          } else if (e.target.closest('[data-cart-decrease]')) {
            self.changeByKey(key, Math.max(0, current - 1));
          }
        });
      }
    }
  };

  window.ProvilanCart = Cart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Cart.init(); });
  } else {
    Cart.init();
  }
})();
